<template>
  <div class="account-page">
    <div class="account-heading">
      <h2 class="account-title">account settings</h2>
      <modal-help topic="account-settings" label="Help for this screen" show-label />
    </div>
    <p class="account-subtitle">The name, email, password, sign-in session, and authenticator for this account. Each change asks for your current password.</p>
    <resource-notice />

    <p v-if="loadError" class="account-error">{{ loadError }}</p>
    <p v-else-if="!loaded" class="account-muted">loading account…</p>

    <div v-else class="account-sections">
      <section v-if="canManagePeople" class="account-section section-wide">
        <h3 class="section-title">people</h3>
        <p class="section-copy">Invite someone who already signs in to the designer. They keep their own email and password, then open this account and design its apps. A designer can use the designer and cannot open billing. Billing can open both.</p>
        <ul v-if="seats.length" class="seat-list">
          <li v-for="seat in seats" :key="seat.email" class="seat-row">
            <span class="seat-email">{{ seat.email }}</span>
            <span class="seat-role">{{ seat.role }}</span>
            <button type="button" class="save-button secondary-button seat-remove" :disabled="seatBusy" @click="removeSeat(seat.email)">remove</button>
          </li>
        </ul>
        <p v-else class="field-note">No one else builds on this account yet.</p>
        <form class="seat-form" @submit.prevent="inviteSeat">
          <label class="field-label" for="seat-email">email</label>
          <input id="seat-email" v-model="inviteEmail" type="email" autocomplete="off" class="field-input" />
          <label class="field-label" for="seat-role">role</label>
          <select id="seat-role" v-model="inviteRole" class="field-input">
            <option value="designer">designer</option>
            <option value="billing">billing</option>
          </select>
          <p v-if="seatError" class="account-error">{{ seatError }}</p>
          <p v-if="seatStatus" class="account-status">{{ seatStatus }}</p>
          <button type="submit" class="save-button" :disabled="seatBusy || !inviteEmail">{{ seatBusy ? 'saving…' : 'invite' }}</button>
        </form>
      </section>
      <section v-if="role === 'designer'" class="account-section section-wide">
        <h3 class="section-title">designer access</h3>
        <p class="section-copy">You can design the apps on this account. Billing and account settings stay closed.</p>
      </section>
      <p v-else-if="role === 'billing'" class="section-copy billing-note">You can open billing for this account. The owner still changes the account name, email, password, and authenticator.</p>
      <template v-if="canEditAccount">
      <form class="account-section" @submit.prevent="saveName">
        <h3 class="section-title">account name</h3>
        <p class="section-copy">This name is the address for the account.</p>
        <label class="field-label" for="account-name">name</label>
        <input id="account-name" v-model="name" type="text" maxlength="63" autocapitalize="none" autocorrect="off" spellcheck="false" autocomplete="off" class="field-input" />
        <p v-if="accountUrl" class="field-hint"><span>address</span> {{ accountUrl }}</p>
        <p v-else-if="liveNameError" class="account-error">{{ liveNameError }}</p>
        <p v-if="nameMoves" class="field-note">Saving this name moves the account to that address.</p>
        <label class="field-label" for="name-password">current password</label>
        <input id="name-password" v-model="namePassword" type="password" autocomplete="current-password" class="field-input" />
        <p v-if="nameError" class="account-error">{{ nameError }}</p>
        <p v-if="nameStatus" class="account-status">{{ nameStatus }}</p>
        <button type="submit" class="save-button" :disabled="nameSaving || !canSaveName">{{ nameSaving ? 'saving…' : 'save name' }}</button>
      </form>

      <form class="account-section" @submit.prevent="saveEmail">
        <h3 class="section-title">email</h3>
        <p class="section-copy">This is the email used to sign in.</p>
        <label class="field-label" for="account-email">email</label>
        <input id="account-email" v-model="email" type="email" autocomplete="email" class="field-input" />
        <label class="field-label" for="email-password">current password</label>
        <input id="email-password" v-model="emailPassword" type="password" autocomplete="current-password" class="field-input" />
        <p v-if="emailError" class="account-error">{{ emailError }}</p>
        <p v-if="emailStatus" class="account-status">{{ emailStatus }}</p>
        <button type="submit" class="save-button" :disabled="emailSaving || !canSaveEmail">{{ emailSaving ? 'saving…' : 'save email' }}</button>
      </form>

      <form class="account-section" @submit.prevent="savePassword">
        <h3 class="section-title">password</h3>
        <p class="section-copy">Choose a new password. The confirmation has to match.</p>
        <label class="field-label" for="new-password">new password</label>
        <input id="new-password" v-model="password" type="password" autocomplete="new-password" class="field-input" />
        <label class="field-label" for="confirm-password">confirm password</label>
        <input id="confirm-password" v-model="confirmPassword" type="password" autocomplete="new-password" class="field-input" />
        <label class="field-label" for="password-password">current password</label>
        <input id="password-password" v-model="passwordPassword" type="password" autocomplete="current-password" class="field-input" />
        <p v-if="passwordMismatch" class="account-error">passwords do not match</p>
        <p v-if="passwordError" class="account-error">{{ passwordError }}</p>
        <p v-if="passwordStatus" class="account-status">{{ passwordStatus }}</p>
        <button type="submit" class="save-button" :disabled="passwordSaving || !canSavePassword">{{ passwordSaving ? 'saving…' : 'save password' }}</button>
      </form>

      <form class="account-section" @submit.prevent="saveSession">
        <h3 class="section-title">sign-in session</h3>
        <p class="section-copy">How long this account stays signed in, and whether it signs out after you stop using it. This is the account that opens the designer. People who sign in inside an app use that app's Users settings.</p>
        <label class="field-label" for="session-count">session length</label>
        <div class="session-row">
          <input id="session-count" v-model.number="sessionCount" type="number" min="1" class="field-input session-count" />
          <select v-model="sessionUnit" class="field-input session-unit" aria-label="session length unit">
            <option value="minutes">minutes</option>
            <option value="hours">hours</option>
            <option value="days">days</option>
          </select>
        </div>
        <label class="session-check" for="inactivity-enabled">
          <input id="inactivity-enabled" v-model="inactivityEnabled" type="checkbox" />
          sign out after inactivity
        </label>
        <div v-if="inactivityEnabled" class="session-row">
          <input id="inactivity-count" v-model.number="inactivityCount" type="number" min="1" class="field-input session-count" aria-label="inactivity timeout" />
          <select v-model="inactivityUnit" class="field-input session-unit" aria-label="inactivity unit">
            <option value="minutes">minutes</option>
            <option value="hours">hours</option>
            <option value="days">days</option>
          </select>
        </div>
        <label class="field-label" for="session-password">current password</label>
        <input id="session-password" v-model="sessionPassword" type="password" autocomplete="current-password" class="field-input" />
        <p v-if="sessionError" class="account-error">{{ sessionError }}</p>
        <p v-if="sessionStatus" class="account-status">{{ sessionStatus }}</p>
        <button type="submit" class="save-button" :disabled="sessionSaving || !sessionPassword">{{ sessionSaving ? 'saving…' : 'save sign-in session' }}</button>
      </form>

      <section class="account-section section-wide">
        <h3 class="section-title">authenticator</h3>
        <p class="section-copy">Optional. When this is on, signing in to this account and the designer asks for a code from an authenticator app after the password.</p>

        <template v-if="twoFactorPhase === 'codes'">
          <p class="section-copy">Save these recovery codes. Each one signs you in once if you lose the authenticator. This is the only time they are shown.</p>
          <ul class="recovery-codes">
            <li v-for="entry in recoveryCodes" :key="entry">{{ entry }}</li>
          </ul>
          <button type="button" class="save-button secondary-button" @click="copyRecoveryCodes">{{ copyStatus || 'copy codes' }}</button>
          <label class="session-check" for="codes-saved">
            <input id="codes-saved" v-model="codesSaved" type="checkbox" />
            I saved these codes
          </label>
          <button type="button" class="save-button" :disabled="!codesSaved" @click="finishRecoveryCodes">done</button>
        </template>

        <template v-else-if="twoFactorPhase === 'scan'">
          <p class="section-copy">Scan this code with an authenticator app, or enter the key, then type the 6-digit code.</p>
          <img v-if="qrDataUrl" class="qr-frame" :src="qrDataUrl" alt="Authenticator setup QR code" />
          <p class="field-hint">key</p>
          <p class="secret-key">{{ groupedSecret }}</p>
          <label class="field-label" for="authenticator-code">authenticator code</label>
          <input id="authenticator-code" v-model="twoFactorCode" type="text" inputmode="numeric" autocomplete="one-time-code" spellcheck="false" class="field-input" />
          <p v-if="twoFactorError" class="account-error">{{ twoFactorError }}</p>
          <div class="button-row">
            <button type="button" class="save-button" :disabled="twoFactorBusy || !twoFactorCode" @click="confirmAuthenticator">{{ twoFactorBusy ? 'verifying…' : 'turn on authenticator' }}</button>
            <button type="button" class="save-button secondary-button" :disabled="twoFactorBusy" @click="cancelAuthenticator">cancel</button>
          </div>
        </template>

        <template v-else-if="!twoFactorEnabled">
          <label class="field-label" for="authenticator-password">current password</label>
          <input id="authenticator-password" v-model="twoFactorPassword" type="password" autocomplete="current-password" class="field-input" />
          <p v-if="twoFactorError" class="account-error">{{ twoFactorError }}</p>
          <p v-if="twoFactorStatus" class="account-status">{{ twoFactorStatus }}</p>
          <button type="button" class="save-button" :disabled="twoFactorBusy || !twoFactorPassword" @click="startAuthenticator">{{ twoFactorBusy ? 'preparing…' : 'set up authenticator' }}</button>
        </template>

        <template v-else>
          <p class="field-note">Authenticator is on. {{ recoveryCodesRemaining }} recovery {{ recoveryCodesRemaining === 1 ? 'code' : 'codes' }} left.</p>
          <form class="nested-form" @submit.prevent="disableAuthenticator">
            <h4 class="nested-title">turn off</h4>
            <label class="field-label" for="disable-password">current password</label>
            <input id="disable-password" v-model="disablePassword" type="password" autocomplete="current-password" class="field-input" />
            <label class="field-label" for="disable-code">authenticator or recovery code</label>
            <input id="disable-code" v-model="disableCode" type="text" autocomplete="one-time-code" spellcheck="false" class="field-input" />
            <p v-if="disableError" class="account-error">{{ disableError }}</p>
            <button type="submit" class="save-button" :disabled="disableBusy || !disablePassword || !disableCode">{{ disableBusy ? 'turning off…' : 'turn off authenticator' }}</button>
          </form>
          <form class="nested-form" @submit.prevent="replaceRecoveryCodes">
            <h4 class="nested-title">new recovery codes</h4>
            <p class="section-copy">This replaces the codes you have now. The old ones stop working.</p>
            <label class="field-label" for="recovery-password">current password</label>
            <input id="recovery-password" v-model="recoveryPassword" type="password" autocomplete="current-password" class="field-input" />
            <label class="field-label" for="recovery-code">authenticator or recovery code</label>
            <input id="recovery-code" v-model="recoveryCode" type="text" autocomplete="one-time-code" spellcheck="false" class="field-input" />
            <p v-if="recoveryError" class="account-error">{{ recoveryError }}</p>
            <button type="submit" class="save-button" :disabled="recoveryBusy || !recoveryPassword || !recoveryCode">{{ recoveryBusy ? 'replacing…' : 'create new recovery codes' }}</button>
          </form>
        </template>
      </section>
      </template>
    </div>
  </div>
</template>

<script>

  import axios from 'axios'
  import { accountNameError, normalizeAccountName, tenantBaseUrl } from '../../../superturbodb_manager_server/modules/account-name.js'
  import { minutesToParts, partsToMinutes, normalizeUserSession, userSessionForRead } from '../../../shared/user-session-policy.js'
  import { canEditAccount as accountEditable, canManageSeats as seatsManageable } from '../../../shared/account-seat.js'
  import { authenticatorQr } from '../lib/authenticator-qr.js'
  import ResourceNotice from './resource-notice.vue'

  export default {
    components: { ResourceNotice },
    data () {

      return {
        loaded: false,
        loadError: ``,
        savedName: ``,
        savedEmail: ``,
        name: ``,
        namePassword: ``,
        nameError: ``,
        nameStatus: ``,
        nameSaving: false,
        email: ``,
        emailPassword: ``,
        emailError: ``,
        emailStatus: ``,
        emailSaving: false,
        password: ``,
        confirmPassword: ``,
        passwordPassword: ``,
        passwordError: ``,
        passwordStatus: ``,
        passwordSaving: false,
        sessionCount: 30,
        sessionUnit: `days`,
        inactivityEnabled: false,
        inactivityCount: 15,
        inactivityUnit: `minutes`,
        sessionPassword: ``,
        sessionError: ``,
        sessionStatus: ``,
        sessionSaving: false,
        twoFactorEnabled: false,
        recoveryCodesRemaining: 0,
        twoFactorPhase: `idle`,
        twoFactorPassword: ``,
        twoFactorCode: ``,
        twoFactorError: ``,
        twoFactorStatus: ``,
        twoFactorBusy: false,
        qrDataUrl: ``,
        manualSecret: ``,
        recoveryCodes: [],
        codesSaved: false,
        copyStatus: ``,
        disablePassword: ``,
        disableCode: ``,
        disableError: ``,
        disableBusy: false,
        recoveryPassword: ``,
        recoveryCode: ``,
        recoveryError: ``,
        recoveryBusy: false,
        role: `owner`,
        seats: [],
        inviteEmail: ``,
        inviteRole: `designer`,
        seatError: ``,
        seatStatus: ``,
        seatBusy: false
      }
    },
    computed: {
      liveNameError () {

        if (!String(this.name || ``).trim()) {

          return ``
        }

        return accountNameError(this.name)
      },
      accountUrl () {

        if (this.liveNameError) {

          return ``
        }

        const label = normalizeAccountName(this.name)

        if (!label) {

          return ``
        }

        const parent = typeof VUE_APP_TENANT_PARENT_DOMAIN === `undefined` ? `localhost` : VUE_APP_TENANT_PARENT_DOMAIN
        const port = typeof VUE_APP_TENANT_HTTP_PORT === `undefined` ? `8080` : VUE_APP_TENANT_HTTP_PORT

        return tenantBaseUrl(label, parent, port)
      },
      nameMoves () {

        return Boolean(this.accountUrl) && normalizeAccountName(this.name) !== this.savedName
      },
      canSaveName () {

        return Boolean(this.nameMoves && this.namePassword && !this.liveNameError)
      },
      canSaveEmail () {

        const next = String(this.email || ``).trim()

        return Boolean(next && next !== this.savedEmail && this.emailPassword)
      },
      canSavePassword () {

        return Boolean(this.password && this.confirmPassword && this.passwordPassword && this.password === this.confirmPassword)
      },
      passwordMismatch () {

        return Boolean(this.password && this.confirmPassword && this.password !== this.confirmPassword)
      },
      canEditAccount () {

        return accountEditable(this.role)
      },
      canManagePeople () {

        return seatsManageable(this.role)
      },
      groupedSecret () {

        return String(this.manualSecret || ``).replace(/(.{4})/g, `$1 `).trim()
      }
    },
    mounted () {

      this.load()
    },
    methods: {
      async load () {

        this.loadError = ``

        try {

          const response = await axios.get(`${VUE_APP_APP_SERVER_URL}/account`, { withCredentials: true })

          this.savedName = response.data.name || ``
          this.savedEmail = response.data.email || ``
          this.name = this.savedName
          this.email = this.savedEmail
          this.role = response.data.role || `owner`
          this.seats = response.data.seats || []
          this.applyOwnerSession(response.data.ownerSession)
          this.applyTwoFactor(response.data)
          this.loaded = true
        } catch (error) {

          this.loadError = this.messageFrom(error, `could not load account settings`)
        }
      },
      async saveName () {

        this.nameError = ``
        this.nameStatus = ``

        const problem = accountNameError(this.name)

        if (problem) {

          this.nameError = problem

          return
        }

        if (!this.namePassword) {

          this.nameError = `enter your current password`

          return
        }

        this.nameSaving = true

        try {

          const response = await axios.put(`${VUE_APP_APP_SERVER_URL}/account`, {
            name: normalizeAccountName(this.name),
            currentPassword: this.namePassword
          }, { withCredentials: true, timeout: 20000 })

          this.namePassword = ``

          if (response.data && response.data.redirectUrl) {

            this.nameStatus = `moving to the new address…`
            const ready = await this.waitForHost(response.data.redirectUrl)

            if (!ready) {

              this.nameStatus = ``
              this.nameError = `the new address did not come up. refresh the page and try again.`
              this.nameSaving = false

              return
            }

            window.location.assign(response.data.redirectUrl)

            return
          }

          this.applyAccount(response.data)
          this.nameStatus = `account name saved`
        } catch (error) {

          this.nameError = this.messageFrom(error, `could not save the account name`)
        } finally {

          if (!this.nameStatus.startsWith(`moving`)) {

            this.nameSaving = false
          }
        }
      },
      async saveEmail () {

        this.emailError = ``
        this.emailStatus = ``

        const next = String(this.email || ``).trim()

        if (!next) {

          this.emailError = `enter an email`

          return
        }

        if (!this.emailPassword) {

          this.emailError = `enter your current password`

          return
        }

        this.emailSaving = true

        try {

          const response = await axios.put(`${VUE_APP_APP_SERVER_URL}/account`, {
            email: next,
            currentPassword: this.emailPassword
          }, { withCredentials: true })

          this.emailPassword = ``
          this.applyAccount(response.data)
          this.emailStatus = `email saved`
        } catch (error) {

          this.emailError = this.messageFrom(error, `could not save the email`)
        } finally {

          this.emailSaving = false
        }
      },
      async savePassword () {

        this.passwordError = ``
        this.passwordStatus = ``

        if (!this.password) {

          this.passwordError = `choose a password`

          return
        }

        if (this.password !== this.confirmPassword) {

          this.passwordError = `passwords do not match`

          return
        }

        if (!this.passwordPassword) {

          this.passwordError = `enter your current password`

          return
        }

        this.passwordSaving = true

        try {

          await axios.put(`${VUE_APP_APP_SERVER_URL}/account`, {
            password: this.password,
            currentPassword: this.passwordPassword
          }, { withCredentials: true })

          this.password = ``
          this.confirmPassword = ``
          this.passwordPassword = ``
          this.passwordStatus = `password saved`
        } catch (error) {

          this.passwordError = this.messageFrom(error, `could not save the password`)
        } finally {

          this.passwordSaving = false
        }
      },
      applyOwnerSession (value) {

        const policy = userSessionForRead(value)
        const session = minutesToParts(policy.sessionMinutes)

        this.sessionCount = session.count
        this.sessionUnit = session.unit
        this.inactivityEnabled = policy.inactivityMinutes != null

        if (policy.inactivityMinutes) {

          const idle = minutesToParts(policy.inactivityMinutes)

          this.inactivityCount = idle.count
          this.inactivityUnit = idle.unit
        }
      },
      ownerSessionPayload () {

        const session = partsToMinutes(this.sessionCount, this.sessionUnit)

        if (session.error) return session

        let inactivityMinutes = null

        if (this.inactivityEnabled) {

          const idle = partsToMinutes(this.inactivityCount, this.inactivityUnit)

          if (idle.error) return idle

          inactivityMinutes = idle.minutes
        }

        return normalizeUserSession({ sessionMinutes: session.minutes, inactivityMinutes })
      },
      async saveSession () {

        this.sessionError = ``
        this.sessionStatus = ``

        if (!this.sessionPassword) {

          this.sessionError = `enter your current password`

          return
        }

        const payload = this.ownerSessionPayload()

        if (payload.error) {

          this.sessionError = payload.error

          return
        }

        this.sessionSaving = true

        try {

          const response = await axios.put(`${VUE_APP_APP_SERVER_URL}/account`, {
            ownerSession: {
              sessionMinutes: payload.sessionMinutes,
              inactivityMinutes: payload.inactivityMinutes
            },
            currentPassword: this.sessionPassword
          }, { withCredentials: true })

          this.sessionPassword = ``
          this.applyOwnerSession(response.data && response.data.ownerSession)
          this.sessionStatus = `sign-in session saved`
        } catch (error) {

          this.sessionError = this.messageFrom(error, `could not save the sign-in session`)
        } finally {

          this.sessionSaving = false
        }
      },
      applyAccount (account) {

        if (!account) {

          return
        }

        if (account.name) {

          this.savedName = account.name
          this.name = account.name
        }

        if (account.email) {

          this.savedEmail = account.email
          this.email = account.email
        }

        this.applyTwoFactor(account)
      },
      applyTwoFactor (account) {

        if (!account || typeof account.twoFactorEnabled !== `boolean`) {

          return
        }

        this.twoFactorEnabled = account.twoFactorEnabled
        this.recoveryCodesRemaining = account.recoveryCodesRemaining || 0
      },
      async startAuthenticator () {

        this.twoFactorError = ``
        this.twoFactorStatus = ``
        this.twoFactorBusy = true

        try {

          const response = await axios.post(`${VUE_APP_APP_SERVER_URL}/account/two-factor/setup`, {
            currentPassword: this.twoFactorPassword
          }, { withCredentials: true })

          this.manualSecret = response.data.secret
          this.twoFactorCode = ``

          try {

            this.qrDataUrl = await authenticatorQr(response.data.otpauthUrl)
          } catch (qrError) {

            this.qrDataUrl = ``
          }
          this.twoFactorPassword = ``
          this.twoFactorPhase = `scan`
        } catch (error) {

          this.twoFactorError = this.messageFrom(error, `could not start authenticator setup`)
        } finally {

          this.twoFactorBusy = false
        }
      },
      async confirmAuthenticator () {

        this.twoFactorError = ``
        this.twoFactorBusy = true

        try {

          const response = await axios.post(`${VUE_APP_APP_SERVER_URL}/account/two-factor/confirm`, {
            code: this.twoFactorCode
          }, { withCredentials: true })

          this.applyTwoFactor(response.data)
          this.recoveryCodes = response.data.recoveryCodes || []
          this.codesSaved = false
          this.copyStatus = ``
          this.twoFactorCode = ``
          this.qrDataUrl = ``
          this.manualSecret = ``
          this.twoFactorPhase = `codes`
        } catch (error) {

          this.twoFactorError = this.messageFrom(error, `that code is not valid`)
        } finally {

          this.twoFactorBusy = false
        }
      },
      cancelAuthenticator () {

        this.twoFactorPhase = `idle`
        this.twoFactorCode = ``
        this.twoFactorError = ``
        this.qrDataUrl = ``
        this.manualSecret = ``
      },
      finishRecoveryCodes () {

        this.recoveryCodes = []
        this.codesSaved = false
        this.copyStatus = ``
        this.twoFactorPhase = `idle`
        this.twoFactorStatus = this.twoFactorEnabled ? `authenticator is on` : ``
      },
      async copyRecoveryCodes () {

        try {

          await navigator.clipboard.writeText(this.recoveryCodes.join(`\n`))
          this.copyStatus = `copied`
        } catch (error) {

          this.copyStatus = `select the codes and copy them`
        }
      },
      async disableAuthenticator () {

        this.disableError = ``
        this.disableBusy = true

        try {

          const response = await axios.post(`${VUE_APP_APP_SERVER_URL}/account/two-factor/disable`, {
            currentPassword: this.disablePassword,
            code: this.disableCode
          }, { withCredentials: true })

          this.applyTwoFactor(response.data)
          this.disablePassword = ``
          this.disableCode = ``
          this.twoFactorStatus = `authenticator is off`
        } catch (error) {

          this.disableError = this.messageFrom(error, `could not turn off the authenticator`)
        } finally {

          this.disableBusy = false
        }
      },
      async replaceRecoveryCodes () {

        this.recoveryError = ``
        this.recoveryBusy = true

        try {

          const response = await axios.post(`${VUE_APP_APP_SERVER_URL}/account/two-factor/recovery-codes`, {
            currentPassword: this.recoveryPassword,
            code: this.recoveryCode
          }, { withCredentials: true })

          this.applyTwoFactor(response.data)
          this.recoveryCodes = response.data.recoveryCodes || []
          this.codesSaved = false
          this.copyStatus = ``
          this.recoveryPassword = ``
          this.recoveryCode = ``
          this.twoFactorPhase = `codes`
        } catch (error) {

          this.recoveryError = this.messageFrom(error, `could not replace the recovery codes`)
        } finally {

          this.recoveryBusy = false
        }
      },
      async waitForHost (redirectUrl) {

        let origin = ``

        try {

          origin = new URL(redirectUrl).origin
        } catch (error) {

          return false
        }

        const deadline = Date.now() + 70000

        while (Date.now() < deadline) {

          try {

            const response = await fetch(`${origin}/health`, { credentials: `omit`, cache: `no-store` })

            if (response.ok) {

              return true
            }
          } catch (error) {

            // The new address is not accepting connections yet.
          }

          await new Promise((resolve) => setTimeout(resolve, 500))
        }

        return false
      },
      async inviteSeat () {

        this.seatError = ``
        this.seatStatus = ``
        this.seatBusy = true

        try {

          const response = await axios.post(`${VUE_APP_APP_SERVER_URL}/account/seats`, {
            email: this.inviteEmail,
            role: this.inviteRole
          }, { withCredentials: true })

          this.seats = (response.data && response.data.seats) || []
          this.inviteEmail = ``
          this.seatStatus = `invited`
        } catch (error) {

          this.seatError = this.messageFrom(error, `could not invite that person`)
        }

        this.seatBusy = false
      },
      async removeSeat (email) {

        this.seatError = ``
        this.seatStatus = ``
        this.seatBusy = true

        try {

          const response = await axios.post(`${VUE_APP_APP_SERVER_URL}/account/seats/remove`, { email }, { withCredentials: true })

          this.seats = (response.data && response.data.seats) || []
          this.seatStatus = `removed`
        } catch (error) {

          this.seatError = this.messageFrom(error, `could not remove that person`)
        }

        this.seatBusy = false
      },
      messageFrom (error, fallback) {

        const data = error.response && error.response.data

        if (typeof data === `string` && data) {

          return data
        }

        return fallback
      }
    }
  }
</script>

<style scoped>

.account-page {
  padding: 28px 32px 48px;
  max-width: 980px;
  overflow: auto;
  color: #e8e8e6;
}

.account-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin: 0 0 6px 0;
}

.account-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: #e8e8e6;
  margin: 0;
}

.account-subtitle {
  font-size: 0.85rem;
  color: #707070;
  margin: 0 0 24px 0;
}

.account-sections {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
  align-items: stretch;
}

.account-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
  height: 100%;
  background-color: #141412;
  border: 1px solid #2a2a27;
  border-radius: 8px;
  padding: 18px 18px 16px;
}

.section-title {
  font-size: 0.95rem;
  font-weight: 600;
  color: #e8e8e6;
  margin: 0;
}

.section-copy {
  font-size: 0.78rem;
  color: #707070;
  margin: 0 0 6px 0;
}

.field-label {
  font-size: 0.75rem;
  font-weight: 500;
  color: #9ad12b;
  letter-spacing: 0.03em;
  margin-top: 4px;
}

.field-input {
  background-color: #181916;
  border: 1px solid #2a2a27;
  border-radius: 5px;
  padding: 10px 12px;
  font-size: 0.85rem;
  color: #e8e8e6;
  outline: none;
  transition: border-color 0.15s;
  font-family: inherit;
  width: 100%;
}

.field-input::placeholder {
  color: #4a4a47;
}

.field-input:focus {
  border-color: #9ad12b;
}

.session-row {
  display: flex;
  gap: 8px;
}

.session-count {
  max-width: 8rem;
}

.session-unit {
  max-width: 10rem;
}

.session-check {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
  font-size: 0.8rem;
  color: #c0c0be;
}

.session-check input {
  width: 14px;
  height: 14px;
  margin: 0;
  accent-color: #9ad12b;
}

.field-hint,
.field-note,
.account-muted {
  color: #707070;
  font-size: 0.75rem;
  margin: 0;
  word-break: break-all;
}

.field-hint span {
  color: #5a5a57;
  margin-right: 6px;
}

.field-note {
  color: #b0b0ae;
}

.account-error {
  color: #e05252;
  font-size: 0.78rem;
  margin: 0;
}

.account-status {
  color: #9ad12b;
  font-size: 0.78rem;
  margin: 0;
}

.save-button {
  margin-top: auto;
  align-self: flex-start;
  padding: 8px 14px;
  background-color: #9ad12b;
  color: #0f0f0d;
  border: 1px solid #9ad12b;
  border-radius: 5px;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
}

.save-button:hover:not(:disabled) {
  background-color: #b8f046;
  border-color: #b8f046;
}

.save-button:disabled {
  background: transparent;
  color: #5a5a57;
  border-color: #2a2a27;
  cursor: default;
}

.section-wide {
  grid-column: 1 / -1;
}

.billing-note {
  grid-column: 1 / -1;
  margin: 0;
}

.seat-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.seat-row {
  display: flex;
  align-items: center;
  gap: 10px;
}

.seat-email {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 0.85rem;
}

.seat-role {
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #9ad12b;
}

.seat-form {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 8px;
}

.seat-remove {
  padding: 4px 10px;
}

.secondary-button {
  background: transparent;
  color: #e8e8e6;
  border-color: #3a3a36;
}

.secondary-button:hover:not(:disabled) {
  background: transparent;
  border-color: #9ad12b;
  color: #9ad12b;
}

.button-row {
  display: flex;
  gap: 8px;
  margin-top: auto;
}

.button-row .save-button {
  margin-top: 0;
}

.qr-frame {
  width: 180px;
  height: 180px;
  background: #fff;
  padding: 8px;
  border-radius: 6px;
}

.secret-key,
.recovery-codes {
  margin: 0;
  color: #e8e8e6;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 0.85rem;
  letter-spacing: 0.04em;
}

.secret-key {
  overflow-wrap: normal;
}

.recovery-codes {
  display: grid;
  grid-template-columns: repeat(2, max-content);
  gap: 8px 24px;
  padding: 0;
  list-style: none;
}

.nested-form {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 8px;
  padding-top: 12px;
  border-top: 1px solid #2a2a27;
}

.nested-title {
  margin: 0;
  font-size: 0.82rem;
  font-weight: 600;
  color: #e8e8e6;
}

@media (max-width: 800px) {
  .account-sections {
    grid-template-columns: 1fr;
  }
}

</style>
